"use client";

import { Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Drawer, DrawerContent, DrawerTitle } from "@/components/ui/drawer";
import {
  type KlipyItem,
  pushRecentGif,
  useGifSearch,
  useRecentGifs,
} from "@/hooks/use-gifs";
import { cn } from "@/lib/utils";
import { CHAT_IG_THEME } from "../chat-theme";
import {
  ChatDrawerHeader,
  ChatSearchInput,
  ChatSegmentedControl,
} from "../chat-ui-primitives";

type PickerTab = "gifs" | "stickers" | "recent";

interface GifPickerDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialTab?: Exclude<PickerTab, "recent">;
  nested?: boolean;
  layer?: number;
  /** Instagram behavior: tap sends immediately. */
  onSelect: (item: KlipyItem, kind: "gif" | "sticker") => void;
}

function GifTile({
  item,
  onSelect,
}: {
  item: KlipyItem;
  onSelect: () => void;
}) {
  const src = item.urls.webp ?? item.urls.gif;
  if (!src) return null;
  const aspect =
    item.width > 0 && item.height > 0 ? item.width / item.height : 1;

  return (
    <button
      type="button"
      onClick={onSelect}
      className="mb-1.5 block w-full overflow-hidden rounded-xl bg-glass-bg-06 transition-transform active:scale-95 dark:bg-white/6"
      style={{ aspectRatio: aspect }}
      aria-label={item.title ?? "Send GIF"}
    >
      {/* biome-ignore lint/performance/noImgElement: animated webp from the KLIPY CDN — next/image optimization would strip animation. */}
      <img
        src={src}
        alt={item.title ?? "GIF"}
        loading="lazy"
        draggable={false}
        className="block h-full w-full object-cover"
      />
    </button>
  );
}

export function GifPickerDrawer({
  open,
  onOpenChange,
  initialTab = "gifs",
  nested = false,
  layer,
  onSelect,
}: GifPickerDrawerProps) {
  const [tab, setTab] = useState<PickerTab>(initialTab);
  const [query, setQuery] = useState("");
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) {
      setTab(initialTab);
    }
  }, [initialTab, open]);

  const remoteKind = tab === "stickers" ? "stickers" : "gifs";
  const search = useGifSearch(remoteKind, tab === "recent" ? "" : query);
  const { data: recents = [] } = useRecentGifs();

  const items =
    tab === "recent"
      ? recents
      : (search.data?.pages.flatMap((page) => page.items) ?? []);

  // Infinite scroll for remote tabs.
  useEffect(() => {
    if (!open || tab === "recent") return;
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver((entries) => {
      if (
        entries[0]?.isIntersecting &&
        search.hasNextPage &&
        !search.isFetchingNextPage
      ) {
        void search.fetchNextPage();
      }
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    open,
    tab,
    search.hasNextPage,
    search.isFetchingNextPage,
    search.fetchNextPage,
  ]);

  const handleSelect = (item: KlipyItem) => {
    // `onSelect` sends through the composer, which plays the send haptic.
    onSelect(item, tab === "stickers" ? "sticker" : "gif");
    void pushRecentGif(item);
    onOpenChange(false);
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) setQuery("");
    onOpenChange(next);
  };

  return (
    <Drawer
      open={open}
      onOpenChange={handleOpenChange}
      repositionInputs
      nested={nested}
    >
      <DrawerContent
        layer={layer ?? (nested ? 120 : undefined)}
        className="h-[78vh] rounded-t-3xl bg-background p-0"
      >
        <DrawerTitle className="sr-only">GIFs and stickers</DrawerTitle>
        <ChatDrawerHeader title="GIFs & stickers" />

        <div className="space-y-2 px-4 pb-2">
          <ChatSegmentedControl
            value={tab}
            onChange={(next) => setTab(next)}
            options={[
              { value: "gifs" as const, label: "GIFs" },
              { value: "stickers" as const, label: "Stickers" },
              { value: "recent" as const, label: "Recent" },
            ]}
          />
          {tab !== "recent" ? (
            <ChatSearchInput
              value={query}
              onChange={setQuery}
              placeholder={
                tab === "stickers" ? "Search stickers" : "Search GIFs"
              }
            />
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 pb-[max(env(safe-area-inset-bottom),1rem)]">
          {tab !== "recent" && search.isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2
                className={cn("h-5 w-5 animate-spin", CHAT_IG_THEME.mutedText)}
              />
            </div>
          ) : items.length === 0 ? (
            <p
              className={cn(
                "py-10 text-center text-sm",
                CHAT_IG_THEME.mutedText,
              )}
            >
              {tab === "recent"
                ? "GIFs you send show up here."
                : "Nothing found. Try another search."}
            </p>
          ) : (
            <div className="columns-2 gap-1.5 sm:columns-3">
              {items.map((item) => (
                <GifTile
                  key={item.slug}
                  item={item}
                  onSelect={() => handleSelect(item)}
                />
              ))}
            </div>
          )}

          {tab !== "recent" ? (
            <div ref={sentinelRef} className="flex justify-center py-3">
              {search.isFetchingNextPage ? (
                <Loader2
                  className={cn(
                    "h-4 w-4 animate-spin",
                    CHAT_IG_THEME.mutedText,
                  )}
                />
              ) : null}
            </div>
          ) : null}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
